import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import Container from "react-bootstrap/esm/Container";
import Tabs from "react-bootstrap/esm/Tabs";
import Tab from "react-bootstrap/esm/Tab";
import Button from "react-bootstrap/esm/Button";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { IconButton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";

import { API } from "../api/API";
import { alertWarning } from "../alert";

const FG_SFG_FIELDS = [
  ["po_number", "PO #", "poNumber", 120],
  ["jtn", "JTN", "jtn", 140],
  ["client", "Client", "client", 140],
  ["reference", "Reference", "reference", 140],
  ["project", "Project", "project", 140],
  ["serial_number", "Serial Number", "serialNumber", 230],
  ["part_number", "Part Number", "partNumber", 140],
  ["part_description", "Part Description", "partDescription", 180],
  ["uom", "UOM", "uom", 90],
  ["bom_quantity", "BOM Quantity", "bomQuantity", 130],
  ["print_status", "Print Status", "printStatus", 130],
  ["production_status", "Production Status", "productionStatus", 160],
  ["assembly_status", "Assembly Status", "assemblyStatus", 150],
  ["load_status", "Load Status", "loadStatus", 130],
  ["created_by", "Created By", "createdBy", 130],
  ["created_at", "Created At", "createdAt", 170],
];

const BOC_FIELDS = [
  ["po_number", "PO #", "poNumber", 120],
  ["jtn", "JTN", "jtn", 140],
  ["client", "Client", "client", 140],
  ["reference", "Reference", "reference", 140],
  ["project", "Project", "project", 140],
  ["box_number", "Box Number", "boxNumber", 230],
  ["print_status", "Print Status", "printStatus", 130],
  ["load_status", "Load Status", "loadStatus", 130],
  ["created_by", "Created By", "createdBy", 130],
  ["created_at", "Created At", "createdAt", 170],
];

const createEmptyFilters = (fields) => {
  const filters = { sNo: "" };

  fields.forEach(([, , filterKey]) => {
    filters[filterKey] = "";
  });

  return filters;
};

export default function PendingReportDetails() {
  const api = useMemo(() => new API(), []);
  const navigate = useNavigate();
  const { salesOrderId } = useParams();

  const [key, setKey] = useState("FG/SFG");
  const [salesOrder, setSalesOrder] = useState(null);
  const [fgSfgData, setFgSfgData] = useState([]);
  const [bocData, setBocData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fgFilters, setFgFilters] = useState(createEmptyFilters(FG_SFG_FIELDS));
  const [bocFilters, setBocFilters] = useState(createEmptyFilters(BOC_FIELDS));

  const loadDetails = async () => {
    setLoading(true);

    try {
      const response = await api.getPendingReportDetails(salesOrderId);

      if (!response?.success) {
        throw new Error(response?.message || "Unable to load report.");
      }

      setSalesOrder(response.sales_order || null);
      setFgSfgData(response.fg_sfg_data || []);
      setBocData(response.boc_data || []);
    } catch (error) {
      console.error("Pending Report details error:", error);

      alertWarning(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch sales order details.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesOrderId]);

  const handleFilterChange = (setter, e) => {
    const { name, value } = e.target;

    setter((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const filterRows = (rows, fields, filters) => {
    return rows.filter((row, index) => {
      const matchesSNo = String(index + 1).includes(filters.sNo || "");

      if (!matchesSNo) return false;

      return fields.every(([field, , filterKey]) => {
        const rowValue = String(row[field] ?? "").toLowerCase();
        const filterValue = String(filters[filterKey] ?? "").toLowerCase();

        return rowValue.includes(filterValue);
      });
    });
  };

  const filteredFgSfgData = filterRows(fgSfgData, FG_SFG_FIELDS, fgFilters);

  const filteredBocData = filterRows(bocData, BOC_FIELDS, bocFilters);

  const createColumns = (fields, filters, setFilters) => {
    const snoColumn = {
      name: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <span>S.No.</span>
          <input
            type="text"
            name="sNo"
            value={filters.sNo}
            onChange={(e) => handleFilterChange(setFilters, e)}
            placeholder="S.No."
            style={{
              marginTop: "5px",
              marginBottom: "2px",
              width: "100%",
              height: "24px",
              padding: "4px",
              textAlign: "center",
              boxSizing: "border-box",
            }}
          />
        </div>
      ),
      selector: (row) => row.s_no,
      sortable: false,
      center: true,
      width: "85px",
    };

    const otherColumns = fields.map(([field, label, filterKey, width]) => ({
      name: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>{label}</span>
          <input
            type="text"
            name={filterKey}
            value={filters[filterKey] || ""}
            onChange={(e) => handleFilterChange(setFilters, e)}
            placeholder={label}
            style={{
              marginTop: "5px",
              marginBottom: "2px",
              width: "100%",
              height: "24px",
              padding: "4px",
              textAlign: "center",
              boxSizing: "border-box",
            }}
          />
        </div>
      ),
      selector: (row) => row[field] ?? "",
      sortable: true,
      center: true,
      width: `${width}px`,
      cell: (row) => (
        <div
          title={String(row[field] ?? "")}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
          }}
        >
          {row[field] ?? ""}
        </div>
      ),
    }));

    return [snoColumn, ...otherColumns];
  };

  const fgSfgColumns = createColumns(FG_SFG_FIELDS, fgFilters, setFgFilters);

  const bocColumns = createColumns(BOC_FIELDS, bocFilters, setBocFilters);

  const handleExport = (rows, fields, fileName, sheetName) => {
    if (!rows.length) {
      alertWarning("No data available to export.");
      return;
    }

    const salesOrderNumber = salesOrder?.sales_order_number || "";

    const excelRows = rows.map((row, index) => {
      const item = {
        "SO #": salesOrderNumber,
        "S.No.": index + 1,
      };

      fields.forEach(([field, label]) => {
        item[label] = row[field] ?? "";
      });

      return item;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelRows);

    worksheet["!cols"] = [
      { wch: 18 }, // SO #
      { wch: 10 }, // S.No.
      ...fields.map(([, label, , width]) => ({
        wch: Math.max(label.length + 2, Math.ceil((width || 120) / 8)),
      })),
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    XLSX.writeFile(workbook, fileName);
  };

  const handleBack = () => {
    navigate("/pending-report");
  };

  return (
    <>
      <div
        className="basic"
        style={{ position: "relative", marginTop: "10px" }}
      />

      <Container fluid>
        {/* Main heading */}
        <div
          style={{
            textAlign: "center",
            marginTop: "10px",
            marginBottom: "8px",
          }}
        >
          <h4 style={{ margin: 0, fontWeight: "bold" }}>Pending Report</h4>
        </div>

        {/* Sales Order heading */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <h5 style={{ margin: 0 }}>
            <b>SO # : {salesOrder?.sales_order_number || ""}</b>
          </h5>
        </div>

        {/* Excel left, Refresh right */}
        <div
          className="mb-3"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Download Excel" placement="top">
              <span>
                <IconButton
                  onClick={() => {
                    if (key === "FG/SFG") {
                      handleExport(
                        filteredFgSfgData,
                        FG_SFG_FIELDS,
                        `${salesOrder?.sales_order_number || "SO"}_FG_SFG_Report.xlsx`,
                        "FG_SFG",
                      );
                    } else {
                      handleExport(
                        filteredBocData,
                        BOC_FIELDS,
                        `${salesOrder?.sales_order_number || "SO"}_BOC_Report.xlsx`,
                        "BOC",
                      );
                    }
                  }}
                  disabled={loading}
                >
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    style={{
                      width: "27px",
                      height: "27px",
                      color: "#243847",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <span style={{ marginLeft: "8px" }}>
              {key === "FG/SFG"
                ? "Download FG/SFG Excel"
                : "Download BOC Excel"}
            </span>
          </div>

          <Tooltip title="Refresh" placement="left">
            <span>
              <IconButton onClick={loadDetails} disabled={loading}>
                <RefreshIcon
                  style={{
                    fontSize: "30px",
                    color: "#243847",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        {/* Report tabs and tables */}
        <Tabs
          activeKey={key}
          onSelect={(selectedKey) => {
            if (selectedKey) setKey(selectedKey);
          }}
          id="pending-report-tabs"
          className="mb-3"
        >
          <Tab eventKey="FG/SFG" title={`FG/SFG (${fgSfgData.length})`}>
            <DataTable
              className="data-table-container"
              columns={fgSfgColumns}
              data={filteredFgSfgData}
              pagination
              responsive
              highlightOnHover
              dense
              progressPending={loading}
              noDataComponent={
                <div style={{ padding: "20px" }}>No FG/SFG records found.</div>
              }
            />
          </Tab>

          <Tab eventKey="BOC" title={`BOC (${bocData.length})`}>
            <DataTable
              className="data-table-container"
              columns={bocColumns}
              data={filteredBocData}
              pagination
              responsive
              highlightOnHover
              dense
              progressPending={loading}
              noDataComponent={
                <div style={{ padding: "20px" }}>No BOC records found.</div>
              }
            />
          </Tab>
        </Tabs>

        {/* Back button below the table */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            marginTop: "15px",
            marginBottom: "20px",
          }}
        >
          <Button
            variant="dark"
            size="sm"
            onClick={handleBack}
            style={{
              backgroundColor: "#243847",
              borderColor: "#243847",
            }}
          >
            Back
          </Button>
        </div>
      </Container>
    </>
  );
}
